import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import AppInput from "../../components/Core/AppInput/AppInput";
import { CreateButton } from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import Pagination from "../../components/Shared/Pagination/Pagination";
import SortIcon from "../../components/Shared/SortIcon/SortIcon";
import InvoiceBatchExportSummaryBadge from "../../components/Shared/InvoiceExportStatus/InvoiceBatchExportSummaryBadge";
import { InvoiceBatchesBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import { INVOICE_BATCH_SORTING_COLUMN, SORT_ORDER } from "../../models/Sorting";
import { useInvoiceBatches } from "../../networking/invoice.hooks";
import { InvoiceBatchListItem } from "../../models/InvoiceBatch";
import { Copy } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { formatRoute } from "../../utils/route.utils";
import { describeInvoiceBatchScope } from "./components/invoiceBatchFilter.utils";

const InvoiceBatchListPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [pageNumber, setPageNumber] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const keyword = useDebounce(searchTerm, 500);
	const [order, setOrder] = useState<SORT_ORDER>();
	const [sort, setSort] = useState<INVOICE_BATCH_SORTING_COLUMN>();

	// Right-click target: cursor position + the batch the row represents
	const [contextMenu, setContextMenu] = useState<{
		x: number;
		y: number;
		invoiceBatch: InvoiceBatchListItem;
	}>();

	const { invoiceBatches, totalInvoiceBatchCount } = useInvoiceBatches({
		page: pageNumber,
		pageSize: PAGE_SIZE,
		keyword,
		sort,
		order,
	});

	const navigateToInvoiceBatchDetails = (invoiceBatchId?: string) => {
		navigate(formatRoute(routes.INVOICE_BATCH_DETAILS, [invoiceBatchId]));
	};

	const handleSortColumn = async (sort?: INVOICE_BATCH_SORTING_COLUMN) => {
		handleSortingOrder();
		setSort(sort);
	};

	const handleSortingOrder = () => {
		if (!order) {
			setOrder(SORT_ORDER.ASC);
		} else if (order === SORT_ORDER.ASC) {
			setOrder(SORT_ORDER.DESC);
		} else if (order === SORT_ORDER.DESC) {
			setOrder(SORT_ORDER.ASC);
		}
	};
	function handleCreateInvoiceBatch(fromId?: string) {
		if (invoiceBatches.length > 0 && invoiceBatches.find((ib) => ib.status === "INITIAL")) {
			window.alert("Cannot create new invoice batch while there is an preview being created");
			return;
		}
		navigate(
			fromId ? `${routes.INVOICE_BATCH_CREATE}?from=${fromId}` : routes.INVOICE_BATCH_CREATE
		);
	}

	return (
		<>
			<Breadcrumbs items={InvoiceBatchesBreadcrumbs} />
			<div className="p-4 px-6">
				<Flex justifyContent="space-between" alignItems="flex-start">
					<AppInput
						containerWidth="200px"
						placeholder={t("SEARCH")}
						icon="search"
						onChange={(e: any) => setSearchTerm(e.target.value)}
					/>{" "}
					<CreateButton onClick={() => handleCreateInvoiceBatch()}>
						{t("invoiceBatch.new")}
					</CreateButton>
				</Flex>

				<div>
					<>
						<TableLayout>
							<table>
								<thead>
									<tr>
										<th onClick={() => handleSortColumn(INVOICE_BATCH_SORTING_COLUMN.TO)}>
											{t("invoiceBatch.to")}
											<SortIcon
												sortType={
													sort === INVOICE_BATCH_SORTING_COLUMN.TO ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
										{/* Not sortable: the cell summarises the whole customer scoping
										    (pools, subsidiaries, tags, contracts) while the backend can
										    only sort on the deprecated single `pool` field, which is set
										    on a batch just when exactly one pool was selected. */}
										<th>{t("batchFilter.scopeColumn")}</th>
										<th onClick={() => handleSortColumn(INVOICE_BATCH_SORTING_COLUMN.CREATED)}>
											{t("CREATED")}

											<SortIcon
												sortType={
													sort === INVOICE_BATCH_SORTING_COLUMN.CREATED ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
										<th onClick={() => handleSortColumn(INVOICE_BATCH_SORTING_COLUMN.STATUS)}>
											{t("STATUS")}
											<SortIcon
												sortType={
													sort === INVOICE_BATCH_SORTING_COLUMN.STATUS ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
									</tr>
								</thead>
								<tbody>
									{invoiceBatches.map((invoiceBatch) => (
										<tr
											key={invoiceBatch.id}
											onClick={() => navigateToInvoiceBatchDetails(invoiceBatch.id)}
											onContextMenu={(e) => {
												e.preventDefault();
												setContextMenu({ x: e.clientX, y: e.clientY, invoiceBatch });
											}}
										>
											<td>{moment(invoiceBatch.to).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
											<td>{describeInvoiceBatchScope(invoiceBatch, t)}</td>
											<td>
												{moment(invoiceBatch.metadata.created).format(
													DATE_TIME_FORMAT_TYPES.DATE_TIME
												)}
											</td>
											<td>
												{t(`invoiceBatch.status.${invoiceBatch.status}`)}
												{invoiceBatch.status === "PROCESSED" && (
													<InvoiceBatchExportSummaryBadge summary={invoiceBatch.exportSummary} />
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</TableLayout>{" "}
						{totalInvoiceBatchCount > PAGE_SIZE && (
							<Pagination
								currentPage={pageNumber}
								totalCount={totalInvoiceBatchCount}
								onClickItem={(e) => setPageNumber(e)}
							/>
						)}
						{contextMenu && (
							<DropdownMenu open onOpenChange={(open) => !open && setContextMenu(undefined)}>
								<DropdownMenuTrigger asChild>
									{/* Invisible anchor pinned at the pointer — same pattern as
									    ShiftContextMenu: a controlled dropdown behaves like a
									    context menu without an extra dependency */}
									<span
										aria-hidden
										style={{
											position: "fixed",
											left: contextMenu.x,
											top: contextMenu.y,
											width: 1,
											height: 1,
										}}
									/>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="start"
									sideOffset={2}
									className="w-64"
									onContextMenu={(e) => e.preventDefault()}
								>
									<DropdownMenuLabel>
										{moment(contextMenu.invoiceBatch.to).format(DATE_TIME_FORMAT_TYPES.DATE)} ·{" "}
										{describeInvoiceBatchScope(contextMenu.invoiceBatch, t)}
									</DropdownMenuLabel>
									<DropdownMenuItem
										onSelect={() => handleCreateInvoiceBatch(contextMenu.invoiceBatch.id)}
									>
										<Copy size={14} className="mr-2" />
										{t("invoiceBatch.newLikeThis")}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</>
				</div>
			</div>
		</>
	);
};

export default InvoiceBatchListPage;
